/**
 * /zh/spec-driven-development — SpecDrivenDevelopment 的中文版。
 * 英文原版见 SpecDrivenDevelopment.tsx。
 */
import { MarketingShell } from './MarketingShell';

export function SpecDrivenDevelopmentZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/spec-driven-development"
      documentTitle="Spec 驱动开发（给 AI agent）· Draftlize"
      documentDescription="Spec 驱动开发让 spec 决定代码——但工作一开始，spec 就和 agent 写出的代码脱节。Draftlize 把 spec 做成 Claude Code、Cursor 经 MCP 读取的卡片，代码一偏离就把状态标成 drift_detected。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · Spec 驱动开发</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 60px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Spec 驱动开发，<br/>
            <em>当代码由 agent 来写。</em>
          </h1>
          <p className="lede">
            Spec 驱动开发把规格当作真相源：你精确地定义要造什么，实现从 spec 推导而来——而且越来越多是由 Claude Code、Cursor 这样的 AI 编码 agent 写的。它一直好用，直到 spec 和代码开始分叉。spec 躺在一份文档里，agent 照着一份副本写，上游某个决策一变，于是 spec、代码、agent 的上下文三者悄悄各说各话——直到出事。Draftlize 把 spec 做成 agent 动手前经 MCP 会先读的可寻址卡片，一旦实现和它对不上，就把状态翻成 <em>drift_detected</em>。
          </p>
          <div style={{ marginTop: 32, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <a className="btn-primary" href="/">用 $5 免费开始</a>
            <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>无需绑卡，按量付费。</span>
          </div>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">文档里的 spec vs <em>agent 会去读的 spec。</em></h2>
          </div>
          <p>Spec 驱动开发的成败，全看 spec 是否还和代码对得上。一份 markdown spec 无法告诉你它什么时候对不上了；一张知道自己依赖的卡片可以。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>文档 / markdown 里的 spec</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>agent 读到的是什么</td><td className="ours yes">经 MCP 读活的卡片</td><td className="no">贴进 prompt 的一份副本</td></tr>
            <tr><td>某个决策变了</td><td className="ours yes">依赖它的 spec 标 stale</td><td className="no">spec 悄悄变错</td></tr>
            <tr><td>实现状态</td><td className="ours yes">自己翻成 drift_detected</td><td className="no">文档上次写的那样</td></tr>
            <tr><td>一个决策，六份 spec</td><td className="ours yes">一张卡片，被六处引用</td><td className="no">六份副本各自漂移</td></tr>
            <tr><td>谁让 spec 和代码对齐</td><td className="ours yes">substrate 替你追踪</td><td className="no">你的记忆</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Spec 驱动开发为什么会<em>崩。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">spec 和代码分叉</h3>
            <p>你写 spec，agent 照着造，第一天两者都对。然后代码在 spec 听不见的一个个 PR 里演进，spec 在代码看不见的一次次编辑里演进。两个真相源，各自漂移——这和没有真相源是一回事。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">agent 照着旧副本写</h3>
            <p>当你把一份 <a href="/zh/product-spec-template" style={{ color: 'var(--amber)' }}>spec</a> 贴进 prompt，agent 是照着复制那一刻冻结的快照在造。之后某个决策一变，agent 还在照旧的实现——而且很自信，因为没人告诉它地基已经动了。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">一个决策变，六份 spec 撒谎</h3>
            <p>翻掉一个 API 契约赖以成立的决策，凡是假设过它的 spec 全错了。没有任何东西把决策连到这些 spec，于是它们读着像真的，直到工程师——或 agent——在 review 里撞上矛盾。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">把 spec 驱动开发放到<em>活的 substrate 上。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">spec 是 agent 先读的卡片</h3>
            <p>Claude Code 或 Cursor 在写下第一行前，经 MCP 把相关 spec 和它底下的决策一并拉出来——于是它实现的是当前定义，而不是上周贴进去的副本。spec 成了 agent 每一轮都读的上下文，而不是只看过一次的文档。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">决策一变，依赖的 spec 标 stale</h3>
            <p>每张 spec 卡片都声明它依赖什么。改一个上游<a href="/zh/decision-log" style={{ color: 'var(--amber)' }}>决策</a>，所有建立在它之上的 spec 自动变 stale——就像构建系统会让某个被改文件的所有下游全部失效。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">实现状态翻成 drift_detected</h3>
            <p>代码写完后 spec 又动了，实现状态就自己翻成 <em>drift_detected</em>——于是 spec 和代码之间的缝，在它裂开那一刻就浮现，而不是三个 sprint 后在复盘里。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Spec 驱动开发有多强，取决于 spec 和代码之间那条链有多牢。把这条链交给 substrate 维护，而不是靠你记着。
        </blockquote>
        <cite>spec 写一次。让 agent 每一轮读到的都是活的那一版。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '什么是 spec 驱动开发？', a: 'Spec 驱动开发是这样一种做法：一份精确的规格是真相源，实现从它推导而来，而不是让设计在你边写边改的代码里自己长出来。在 AI 时代，spec 往往是人写的、代码是 agent 从 spec 生成的，这让「让两者保持同步」成了核心问题。' },
            { q: 'Spec 驱动开发和测试驱动开发（TDD）有什么区别？', a: '测试驱动开发先写一个失败的测试，再写代码让它通过。Spec 驱动开发先写规格，再实现以满足它——而测试可以是这份规格的一种表达。两者互补：spec 说明造什么、为什么，测试检查造出来的是否对得上。' },
            { q: 'Spec 驱动开发适合 AI 编码 agent 吗？', a: '可以说这正是它最要紧的场景。Claude Code 或 Cursor 这样的 agent 照着给它的 spec 实现，所以 spec 的新鲜度直接决定代码。失败模式就是 agent 照着一份旧副本造——而一份可寻址、agent 能读的 spec 正好避开这一点。' },
            { q: '怎么让 spec 和代码保持同步？', a: '别再把 spec 存成静态副本。Draftlize 把 spec 做成 agent 动手前经 MCP 会读的卡片，把每份 spec 接到它依赖的决策上，上游决策一变就把实现状态翻成 drift_detected——于是分叉在发生那一刻就浮现，而不是等到 review。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">给你的 agent 一份<em>一直成立的 spec——用 $5 免费。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账户送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            把 spec 写成卡片，经 MCP 接上 Claude Code 或 Cursor，让实现在决策一动那一刻就标出 drift。看看 <a href="/zh/for-developers" style={{ color: 'var(--amber)' }}>给开发者的 Draftlize</a>。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
